import { useState, useRef, useCallback, type ReactNode } from "react";
import { cn } from "../lib/utils";

interface SplitPaneProps {
  children: [ReactNode, ReactNode];
  direction?: "horizontal" | "vertical";
  defaultSplit?: number;
  minSize?: number;
  maxSize?: number;
  className?: string;
}

export function SplitPane({
  children,
  direction = "horizontal",
  defaultSplit = 50,
  minSize = 80,
  maxSize,
  className,
}: SplitPaneProps) {
  const [split, setSplit] = useState(defaultSplit);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const startDrag = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    isDragging.current = true;

    function onMove(ev: MouseEvent | TouchEvent) {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const clientPos =
        "touches" in ev ? ev.touches[0] : ev as MouseEvent;
      let pct: number;
      if (direction === "horizontal") {
        pct = ((clientPos.clientX - rect.left) / rect.width) * 100;
      } else {
        pct = ((clientPos.clientY - rect.top) / rect.height) * 100;
      }
      const total = direction === "horizontal" ? rect.width : rect.height;
      const minPct = (minSize / total) * 100;
      const maxPct = maxSize ? (maxSize / total) * 100 : 100 - minPct;
      setSplit(Math.min(maxPct, Math.max(minPct, pct)));
    }

    function onUp() {
      isDragging.current = false;
      document.removeEventListener("mousemove", onMove as EventListener);
      document.removeEventListener("touchmove", onMove as EventListener);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchend", onUp);
    }

    document.addEventListener("mousemove", onMove as EventListener);
    document.addEventListener("touchmove", onMove as EventListener);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchend", onUp);
  }, [direction, minSize, maxSize]);

  const isH = direction === "horizontal";
  const [first, second] = children;

  const firstStyle = isH
    ? { width: `${split}%` }
    : { height: `${split}%` };

  return (
    <div
      ref={containerRef}
      className={cn("split-pane", isH ? "split-pane-h" : "split-pane-v", className)}
    >
      <div className="split-pane-panel split-pane-first" style={firstStyle}>
        {first}
      </div>

      <div
        className={cn("split-pane-divider", isH ? "split-pane-divider-h" : "split-pane-divider-v")}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        role="separator"
        aria-orientation={isH ? "vertical" : "horizontal"}
        aria-valuenow={Math.round(split)}
        aria-valuemin={0}
        aria-valuemax={100}
        tabIndex={0}
      />

      <div className="split-pane-panel split-pane-second" style={{ flex: 1 }}>
        {second}
      </div>
    </div>
  );
}
